// Stripe owns the iframe and all card data. Load its SDK only when the user opens checkout.
import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Button, Caption } from '../components/ui';
import BrandLogo from '../components/BrandLogo';

type Props = { clientSecret: string; publishableKey: string; onComplete: () => void };
export default function EmbeddedPayment({ clientSecret, publishableKey, onComplete }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const complete = useRef(onComplete);
  complete.current = onComplete;
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [retry, setRetry] = useState(0);
  const { t } = useTranslation();
  useEffect(() => {
    let disposed = false;
    let checkout: { destroy: () => void } | undefined;
    setState('loading');
    (async () => {
      const { loadStripe } = await import('@stripe/stripe-js/pure');
      const stripe = await loadStripe(publishableKey);
      if (disposed) return;
      if (!stripe) throw new Error('stripe_unavailable');
      const instance = await stripe.createEmbeddedCheckoutPage({ clientSecret, onComplete: () => { if (!disposed) complete.current(); } });
      if (disposed) { instance.destroy(); return; }
      checkout = instance;
      if (host.current) instance.mount(host.current);
      setState('ready');
    })().catch(() => { if (!disposed) setState('error'); });
    return () => { disposed = true; checkout?.destroy(); };
  }, [clientSecret, publishableKey, retry]);
  return <View style={{ gap: 16 }}>
    {state === 'loading' ? <BrandLogo loading label={t('super.loading')} /> : null}
    {state === 'error' ? <><Caption>{t('checkout.error')}</Caption><Button label={t('checkout.retry')} onPress={() => setRetry(v => v + 1)} /></> : null}
    <div ref={host} aria-label={t('checkout.secureForm')} />
  </View>;
}
